import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, FileDown, FileText as FileTextIcon, ClipboardCheck, BarChart3, ScrollText } from "lucide-react";
import { toast } from "sonner";
import {
  generateExamPackage,
  type ExamPackage,
  type ExamType,
  type Difficulty,
} from "@/lib/exam-generator.functions";
import { downloadExamPDF, downloadExamDOCX } from "@/lib/exam-exports";

export const Route = createFileRoute("/teacher/exams")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "AI Exam Generator · MAKAI Teachers" }] }),
  component: Page,
});

const EXAM_TYPES: { value: ExamType; label: string; hint: string }[] = [
  { value: "beginning", label: "Beginning of Term", hint: "Diagnostic — previous term content" },
  { value: "mid", label: "Mid-Term", hint: "First half of current term" },
  { value: "end", label: "End of Term", hint: "Full term syllabus" },
  { value: "mock", label: "Mock", hint: "UNEB-style trial paper" },
];
const DIFFICULTIES: { value: Difficulty; label: string }[] = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
  { value: "mixed", label: "Mixed (recommended)" },
];
const MARK_PRESETS = [40, 50, 60, 80, 100, 120, 150];
const DURATIONS = [40, 60, 90, 120, 150, 180];

function Page() {
  const ctx = useTeacherContext();
  const generate = useServerFn(generateExamPackage);

  const [examType, setExamType] = useState<ExamType>("end");
  const [subject, setSubject] = useState("");
  const [className, setClassName] = useState("");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState<Difficulty>("mixed");
  const [totalMarks, setTotalMarks] = useState(100);
  const [duration, setDuration] = useState(120);
  const [term, setTerm] = useState("Term 1");
  const [year, setYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState<ExamPackage | null>(null);

  const subjectOptions = useMemo(
    () => Array.from(new Set(ctx.subjects.map((s) => s.name))).filter(Boolean),
    [ctx.subjects]
  );
  const classOptions = useMemo(
    () => Array.from(new Set(ctx.streams.map((s) => `${s.class_name} ${s.name}`))),
    [ctx.streams]
  );

  async function run() {
    if (!subject || !className || !topic) {
      toast.error("Please fill subject, class and topic / scope.");
      return;
    }
    setBusy(true); setPkg(null);
    const t = toast.loading("MAKAI is setting your exam paper…");
    try {
      const out = await generate({
        data: {
          examType, subject, className, topic, difficulty,
          totalMarks, durationMins: duration, term, year,
          schoolName: ctx.schoolName ?? undefined,
          notes: notes || undefined,
        },
      });
      setPkg(out);
      toast.success("Exam paper ready", { id: t });
    } catch (e) {
      toast.error((e as Error).message ?? "Generation failed", { id: t });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell role="teacher" title="AI Exam Generator" subtitle="MAKAI sets exams, marking guides and Bloom's analysis">
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <Card className="h-fit lg:sticky lg:top-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ScrollText className="h-4 w-4 text-primary" /> Exam brief
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Exam type</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {EXAM_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setExamType(t.value)}
                    className={`rounded-md border p-2 text-left text-xs transition ${
                      examType === t.value ? "border-primary bg-primary/5 text-primary" : "hover:bg-accent"
                    }`}
                  >
                    <div className="font-semibold">{t.label}</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">{t.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Subject</Label>
              {subjectOptions.length ? (
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger><SelectValue placeholder="Choose subject" /></SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    <SelectItem value="__other">Other…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Mathematics" />
              )}
              {subject === "__other" && <Input className="mt-2" placeholder="Type subject" onChange={(e) => setSubject(e.target.value)} />}
            </div>

            <div className="space-y-1.5">
              <Label>Class</Label>
              {classOptions.length ? (
                <Select value={className} onValueChange={setClassName}>
                  <SelectTrigger><SelectValue placeholder="Choose class" /></SelectTrigger>
                  <SelectContent>
                    {classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__other">Other…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="e.g. P7 or S4" />
              )}
              {className === "__other" && <Input className="mt-2" placeholder="Type class" onChange={(e) => setClassName(e.target.value)} />}
            </div>

            <div className="space-y-1.5">
              <Label>Topic / scope</Label>
              <Textarea rows={2} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Fractions, decimals & percentages; OR full Term 2 syllabus" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Difficulty</Label>
                <Select value={difficulty} onValueChange={(v) => setDifficulty(v as Difficulty)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Total marks</Label>
                <Select value={String(totalMarks)} onValueChange={(v) => setTotalMarks(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MARK_PRESETS.map((m) => <SelectItem key={m} value={String(m)}>{m} marks</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Duration</Label>
                <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Term</Label>
                <Select value={term} onValueChange={setTerm}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Term 1">Term 1</SelectItem>
                    <SelectItem value="Term 2">Term 2</SelectItem>
                    <SelectItem value="Term 3">Term 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Year</Label>
              <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} />
            </div>

            <div className="space-y-1.5">
              <Label>Extra notes (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Skip calculators; focus on word problems; etc." />
            </div>

            <Button onClick={run} disabled={busy} className="w-full gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy ? "Setting paper…" : "Generate exam"}
            </Button>
            <p className="text-xs text-muted-foreground">UNEB-style, NCDC-aligned. Always review before printing.</p>
          </CardContent>
        </Card>

        <div className="min-w-0">
          {!pkg && !busy && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
                <ScrollText className="h-10 w-10 text-primary/60" />
                <p className="max-w-md text-sm">Fill the brief on the left. MAKAI will draft a complete exam paper, marking guide and Bloom's taxonomy analysis — exportable as PDF or Word.</p>
              </CardContent>
            </Card>
          )}
          {busy && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm">Setting your exam paper — this can take 20-40s for longer papers.</p>
              </CardContent>
            </Card>
          )}
          {pkg && <Result pkg={pkg} />}
        </div>
      </div>
    </AppShell>
  );
}

function Result({ pkg }: { pkg: ExamPackage }) {
  const sumMarks = pkg.questions.reduce((s, q) => s + (q.marks || 0), 0);
  const marksOk = sumMarks === pkg.meta.totalMarks;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{pkg.meta.school}</div>
            <CardTitle className="text-xl">{pkg.meta.examTypeLabel}</CardTitle>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary">{pkg.meta.subject}</Badge>
              <Badge variant="secondary">{pkg.meta.class}</Badge>
              <Badge variant="secondary">{pkg.meta.term} · {pkg.meta.year}</Badge>
              <Badge variant="secondary">{pkg.meta.durationMins} min</Badge>
              <Badge>{pkg.meta.totalMarks} marks</Badge>
              <Badge variant="outline">{pkg.meta.paperCode}</Badge>
              {!marksOk && <Badge variant="destructive">Marks sum: {sumMarks}</Badge>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadExamPDF(pkg, "paper")}>
              <FileDown className="h-4 w-4" /> Paper PDF
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadExamDOCX(pkg, "paper")}>
              <FileTextIcon className="h-4 w-4" /> Paper Word
            </Button>
            <Button size="sm" className="gap-1.5" onClick={() => downloadExamPDF(pkg, "marking")}>
              <ClipboardCheck className="h-4 w-4" /> Full PDF + Marking
            </Button>
            <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => downloadExamDOCX(pkg, "marking")}>
              <ClipboardCheck className="h-4 w-4" /> Full Word + Marking
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="paper" className="w-full">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="paper">Question Paper</TabsTrigger>
          <TabsTrigger value="marking">Marking Guide</TabsTrigger>
          <TabsTrigger value="bloom">Bloom's Analysis</TabsTrigger>
          <TabsTrigger value="meta">Coverage & Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="paper" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Instructions to candidates</CardTitle></CardHeader>
            <CardContent>
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                {pkg.instructions.map((i, idx) => <li key={idx}>{i}</li>)}
              </ol>
            </CardContent>
          </Card>

          {pkg.sections?.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {pkg.sections.map((s) => (
                <Card key={s.name}><CardContent className="pt-4">
                  <div className="flex justify-between"><strong className="text-sm">{s.name}</strong><Badge variant="outline">{s.marks} mk</Badge></div>
                  <p className="text-xs text-muted-foreground mt-1">{s.description}</p>
                </CardContent></Card>
              ))}
            </div>
          )}

          <Questions pkg={pkg} showAnswers={false} />
        </TabsContent>

        <TabsContent value="marking" className="pt-4">
          <Questions pkg={pkg} showAnswers />
        </TabsContent>

        <TabsContent value="bloom" className="pt-4 space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm uppercase tracking-wide text-muted-foreground"><BarChart3 className="h-4 w-4" /> Bloom's taxonomy distribution</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {pkg.bloomAnalysis.map((b) => (
                <div key={b.level} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{b.level}</span>
                    <span className="text-muted-foreground text-xs">{b.count} Qs · {b.marks} mk · {b.percentage}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, b.percentage)}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          {pkg.difficultyAnalysis && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Difficulty</CardTitle></CardHeader>
              <CardContent className="text-sm">
                Dominant level: <Badge>{pkg.difficultyAnalysis.level}</Badge>{" "}
                <span className="text-muted-foreground">— {pkg.difficultyAnalysis.count} questions ({pkg.difficultyAnalysis.percentage}%)</span>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="meta" className="pt-4 space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Topic coverage</CardTitle></CardHeader>
            <CardContent>
              <ul className="list-disc pl-5 space-y-1 text-sm">{pkg.coverage.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Examiner's notes</CardTitle></CardHeader>
            <CardContent><p className="text-sm whitespace-pre-wrap">{pkg.examinerNotes}</p></CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Questions({ pkg, showAnswers }: { pkg: ExamPackage; showAnswers: boolean }) {
  let currentSection = "";
  return (
    <div className="space-y-3">
      {pkg.questions.map((q) => {
        const isNewSection = q.section && q.section !== currentSection;
        if (isNewSection) currentSection = q.section;
        return (
          <div key={q.number}>
            {isNewSection && <h3 className="text-primary font-semibold mt-4 mb-2 text-sm uppercase tracking-wide">{q.section}</h3>}
            <Card>
              <CardContent className="pt-4 text-sm space-y-2">
                <div className="flex justify-between gap-3">
                  <div className="font-medium"><span className="text-primary">{q.number}.</span> {q.question}</div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant="outline">{q.marks} mk</Badge>
                    <Badge variant="secondary" className="text-[10px]">{q.bloom}</Badge>
                  </div>
                </div>
                {q.type === "MCQ" && q.options && (
                  <ul className="space-y-0.5 pl-3">
                    {q.options.map((o, oi) => {
                      const isCorrect = showAnswers && (o === q.answer || String.fromCharCode(65 + oi) === q.answer);
                      return (
                        <li key={oi} className={isCorrect ? "text-primary font-medium" : ""}>
                          {String.fromCharCode(65 + oi)}. {o}{isCorrect && " ✓"}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {showAnswers && (
                  <div className="mt-2 rounded-md bg-muted/50 p-2 space-y-1">
                    <div className="text-xs"><strong className="text-primary">Answer:</strong> {q.answer}</div>
                    {q.markingPoints?.length > 0 && (
                      <ul className="text-xs space-y-0.5 list-none">
                        {q.markingPoints.map((m, i) => <li key={i}>✓ {m}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
}
